<template>
  <v-card
    class="pa-4 rounded-xl extension-card"
    elevation="4"
    color="secondary"
    :disabled="item.status === 'not_found'"
  >
    <template v-if="item.status === 'found'">
      <v-row>
        <v-col cols="12" md="12">
          <div class="card-header">
            <div class="ingredientImageContainer">
              <v-img
                :src="item.productImage"
                width="100%"
                height="120"
                class="rounded-lg extension-image"
                contain
              />
            </div>
            <div class="header-meta">
            <div class="nova-chip">
              <v-chip
                v-if="item.novaGroup?.string"
                :color="item.novaGroup.color"
                label
                small
                outlined
              >
                {{ $t(item.novaGroup.string) }}
              </v-chip>
            </div>
            <div class="nutri-score-wrapper">
              <div :class="['nutri-score-pill', nutriScoreClass]">
                <span class="nutri-score-letter">{{ nutriScoreLetter || "—" }}</span>
              </div>
              <span class="nutri-score-label">Nutri-Score</span>
            </div>
            <div class="header-title">{{ localItem.productName || "Nom non disponible" }}</div>
            <div class="header-subtitle">{{ localItem.productBrand || "Marque non disponible" }}</div>
          </div>
          </div>

          <div class="mt-2" v-if="hasUnsafeIntolerances">
            <div class="warning-banner">
              <v-icon size="small" class="mr-2">mdi-alert-circle</v-icon>
              <span>
                Produit incompatible avec vos intolérances :
                <strong>{{ unsafeIntolerancesLabel }}</strong>
              </span>
            </div>
          </div>

          <div class="mt-2" v-if="hasUnsafeAllergens">
            <div class="warning-banner allergen-warning">
              <v-icon size="small" class="mr-2">mdi-alert</v-icon>
              <span>
                Allergènes incompatibles avec votre profil :
                <strong>{{ unsafeAllergensLabel }}</strong>
              </span>
            </div>
          </div>
          
          <div class="form-grid">
            <v-text-field
              v-model="localItem.productImage"
              label="URL de l'image"
              variant="underlined"
              density="comfortable"
              class="field-span-2"
            />
            <v-text-field
              v-model="localItem.productName"
              label="Nom du produit"
              variant="underlined"
              density="comfortable"
              :error="!localItem.productName"
              class="field-span-2"
            />
            <v-text-field
              v-model="localItem.productBrand"
              label="Marque"
              variant="underlined"
              density="comfortable"
              :error="!localItem.productBrand"
            />
            <v-text-field
              v-model="localItem.servingQuantity"
              label="Portion (g)"
              type="number"
              variant="underlined"
              density="comfortable"
              :error="!localItem.servingQuantity"
            />
            <v-text-field
              v-model="localItem.servingSize"
              label="Taille de portion"
              variant="underlined"
              density="comfortable"
              :error="!localItem.servingSize"
              class="field-span-2"
            />
          </div>

          <div class="mt-3">
            <h3 class="section-title">Allergènes</h3>
            <div v-if="hasAllergens" class="allergens-banner mt-1">
              <v-icon size="small" class="mr-2">mdi-alert</v-icon>
              <span>{{ safeAllergens.map((a) => $t(a)).join(", ") }}</span>
            </div>
            <div v-else class="mt-1 text-body-2 text-success">
              ✓ Aucun allergène connu
            </div>
          </div>
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <div class="d-flex align-center">
        <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
        <div>
          <div class="text-subtitle-1">Entrée non valide</div>
          <div class="text-body-2 opacity-70">
            Aucune donnée exploitable pour {{ item.barcode || "—" }}
          </div>
        </div>
      </div>
    </template>

    <v-divider class="my-4" />

    <div class="d-flex justify-space-between">
      <v-chip size="small" variant="tonal" class="mr-2" color="grey">
        {{ item.barcode }}
      </v-chip>

      <div class="d-flex align-center">
        <v-btn
          size="small"
          variant="text"
          color="error"
          @click="$emit('remove')"
        >
          <v-icon size="18" class="mr-1">mdi-delete</v-icon>
          Supprimer
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script>
import { useAuthStore } from "@/store/auth_store";
import { checkProductWithIntolerances } from "@/service/intolerance_service";

export default {
  name: "ExtensionResultCard",
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  emits: ['remove', 'update:item'],
  data() {
    return {
      localItem: { ...this.item }
    };
  },
  watch: {
    item: {
      handler(newVal) {
        this.localItem = { ...newVal };
      },
      deep: true
    },
    localItem: {
      handler(newVal) {
        this.$emit('update:item', newVal);
      },
      deep: true
    }
  },
  computed: {
    authStore() {
      return useAuthStore();
    },
    productInfoForIntolerance() {
      const base = this.item?.foodInfo ? { ...this.item.foodInfo } : {};
      const allergens = Array.isArray(this.item?.allergens) ? this.item.allergens : [];
      if (!base.allergens && allergens.length) {
        base.allergens = allergens.join(", ");
      }
      if (!base.allergens_tags && allergens.length) {
        base.allergens_tags = allergens.map((a) =>
          String(a).includes(":") ? String(a) : `en:${a}`
        );
      }
      return Object.keys(base).length ? base : null;
    },
    unsafeIntolerances() {
      const userIntolerances = [
        ...(Array.isArray(this.authStore.userInfos?.intolerances)
          ? this.authStore.userInfos.intolerances
          : []),
        ...(Array.isArray(this.authStore.userInfos?.allergies)
          ? this.authStore.userInfos.allergies
          : []),
      ];
      const productInfo = this.productInfoForIntolerance;
      if (!userIntolerances.length || !productInfo) return [];
      return checkProductWithIntolerances(productInfo, userIntolerances);
    },
    unsafeIntolerancesLabel() {
      return this.unsafeIntolerances.map((i) => `"${this.$t(i)}"`).join(", ");
    },
    unsafeAllergens() {
      const userAllergies = this.authStore.userInfos?.allergies || [];
      const productAllergens = Array.isArray(this.item?.allergens)
        ? this.item.allergens
        : [];
      if (!userAllergies.length || !productAllergens.length) return [];
      const userSet = new Set(userAllergies.map((a) => this.normalizeKey(a)));
      return productAllergens.filter((a) => userSet.has(this.normalizeKey(a)));
    },
    unsafeAllergensLabel() {
      return this.unsafeAllergens.map((a) => `"${this.$t(a)}"`).join(", ");
    },
    safeAllergens() {
      return Array.isArray(this.item?.allergens) ? this.item.allergens : [];
    },
    nutriScoreLetter() {
      const raw = this.item?.nutriscore;
      if (!raw) return "";
      return String(raw).trim().toUpperCase();
    },
    nutriScoreClass() {
      const letter = this.nutriScoreLetter;
      const allowed = ["A", "B", "C", "D", "E"];
      if (allowed.includes(letter)) {
        return `nutri-score-${letter.toLowerCase()}`;
      }
      return "nutri-score-unknown";
    },
    hasAllergens() {
      return this.safeAllergens.length > 0;
    },
    hasUnsafeAllergens() {
      return Array.isArray(this.unsafeAllergens) && this.unsafeAllergens.length > 0;
    },
    hasUnsafeIntolerances() {
      return Array.isArray(this.unsafeIntolerances) && this.unsafeIntolerances.length > 0;
    }
  },
  methods: {
    normalizeKey(value) {
      return String(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    }
  }
};
</script>

<style scoped>
.ingredientImageContainer {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 120px;
  height: 120px;
  border-radius: 12px;
  background: rgba(var(--v-theme-text), 0.06);
  border: 1px solid rgba(var(--v-theme-text), 0.1);
  overflow: hidden;
}
.opacity-70 {
  opacity: 0.7;
}
.extension-card {
  background: rgb(var(--v-theme-secondary));
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.22);
  color: rgb(var(--v-theme-text));
}

.extension-image {
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);
}

.card-header {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 8px;
}

.header-meta {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.nova-chip {
  align-self: flex-start;
}

.nutri-score-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.nutri-score-pill {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.82rem;
  color: rgb(var(--v-theme-text));
  border: 1px solid rgba(var(--v-theme-text), 0.2);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.2);
}

.nutri-score-letter {
  line-height: 1;
  text-transform: uppercase;
}

.nutri-score-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(var(--v-theme-text), 0.65);
}

.nutri-score-a {
  background: rgb(var(--v-theme-accent));
}

.nutri-score-b {
  background: rgba(var(--v-theme-accent), 0.75);
}

.nutri-score-c {
  background: rgba(var(--v-theme-tertiary), 0.9);
}

.nutri-score-d {
  background: rgba(var(--v-theme-error), 0.75);
}

.nutri-score-e {
  background: rgb(var(--v-theme-error));
}

.nutri-score-unknown {
  background: rgba(var(--v-theme-text), 0.18);
}

.header-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}

.header-subtitle {
  font-size: 0.82rem;
  color: rgba(var(--v-theme-text), 0.65);
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
  margin-top: 8px;
}

.field-span-2 {
  grid-column: span 2;
}
.warning-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(var(--v-theme-error), 0.16);
  border: 1px solid rgba(var(--v-theme-error), 0.45);
  color: rgb(var(--v-theme-text));
  font-size: 0.82rem;
  line-height: 1.2;
}

.warning-banner .v-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  position: relative;
  top: -1px;
}

.allergen-warning {
  background: rgba(var(--v-theme-error), 0.2);
  border-color: rgba(var(--v-theme-error), 0.6);
}

.allergens-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(var(--v-theme-tertiary), 0.45);
  border: 1px solid rgba(var(--v-theme-text), 0.18);
  color: rgb(var(--v-theme-text));
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.2;
}

.allergens-banner .v-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  position: relative;
  top: -1px;
}

.section-title {
  color: rgba(var(--v-theme-text), 0.6);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.72rem;
  font-weight: 700;
  margin-bottom: 6px;
}
</style>
